import { supabase } from './supabase'
import type { FiqueRegistro } from './fiqueEmDiaParser'

export type EtapaOferta = { desconto: number; valor_final: number }
export type FiqueCliente = {
  nome: string
  matriculas: string
  saldo: number
  faturas: number
  vencimento: string
  dias: number
  cliente_social: boolean
  max_parcelas: number
  etapas: EtapaOferta[]
}
export type FiqueConsulta = {
  status: 'elegivel' | 'nao_encontrado' | 'invalido' | 'inconsistente' | 'sem_base' | 'limite'
  referencia?: string
  atualizado_em?: string
  registros?: FiqueCliente[]
}
export type FiqueStatus = {
  possui_base: boolean
  referencia: string | null
  atualizado_em: string | null
}
export type FiqueBatch = {
  id: number
  source_filename: string
  checksum?: string
  row_count: number
  reference_date: string
  imported_at: string
}
export type FiqueAdminStatus = {
  active: FiqueBatch | null
  previous: FiqueBatch | null
}

export async function obterStatusFiqueEmDia(): Promise<FiqueStatus> {
  const { data, error } = await supabase.rpc('fique_em_dia_status')
  if (error) throw error
  return data as FiqueStatus
}

export async function consultarFiqueEmDia(cpf: string): Promise<FiqueConsulta> {
  const { data, error } = await supabase.rpc('fique_em_dia_consultar', { p_cpf: cpf })
  if (error) throw error
  return data as FiqueConsulta
}

export async function obterStatusAdminFiqueEmDia(): Promise<FiqueAdminStatus> {
  const { data, error } = await supabase.rpc('fique_em_dia_admin_status')
  if (error) throw error
  return data as FiqueAdminStatus
}

// Lotes pequenos evitam o envio de arquivos de 15–20 MB em uma única chamada HTTP.
// A nova base só fica ativa após TODOS os registros terem sido recebidos e validados no servidor.
export async function substituirBaseFiqueEmDia(
  arquivo: string, checksum: string, referencia: string, registros: FiqueRegistro[],
  onProgress?: (percentual: number) => void,
): Promise<{ id: number; row_count: number; status: string }> {
  const BATCH_SIZE = 750
  const { data: start, error: startError } = await supabase.rpc('fique_em_dia_iniciar_importacao', {
    p_source_filename: arquivo,
    p_checksum: checksum,
    p_reference_date: referencia,
    p_expected_count: registros.length,
  })
  if (startError) throw startError
  const id = (start as { id: number }).id
  try {
    onProgress?.(0)
    for (let offset = 0; offset < registros.length; offset += BATCH_SIZE) {
      const lote = registros.slice(offset, offset + BATCH_SIZE)
      const { error } = await supabase.rpc('fique_em_dia_adicionar_lote', {
        p_batch_id: id,
        p_registros: lote,
      })
      if (error) throw error
      onProgress?.(Math.min(99, Math.floor(100 * (offset + lote.length) / registros.length)))
    }
    const { data, error } = await supabase.rpc('fique_em_dia_publicar_importacao', { p_batch_id: id })
    if (error) throw error
    onProgress?.(100)
    return data as { id: number; row_count: number; status: string }
  } catch (error) {
    // A limpeza é apenas da importação ainda não publicada, nunca da base vigente.
    await supabase.rpc('fique_em_dia_cancelar_importacao', { p_batch_id: id })
    throw error
  }
}

export async function restaurarBaseFiqueEmDia() {
  const { data, error } = await supabase.rpc('fique_em_dia_restaurar_base')
  if (error) throw error
  return data as { id: number; status: string }
}
